import { useCallback, useRef, useState } from 'react';

const MAX_HISTORY = 30;

export const useDrawHistory = (sigPadRef) => {
  const baselineRef = useRef(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const restore = useCallback(
    (dataUrl) => {
      if (!sigPadRef.current) return;
      sigPadRef.current.clear();
      if (!dataUrl) return;
      const canvas = sigPadRef.current.getCanvas();
      sigPadRef.current.fromDataURL(dataUrl, {
        ratio: 1,
        width: canvas.width,
        height: canvas.height
      });
    },
    [sigPadRef]
  );

  const setBaseline = useCallback((dataUrl) => {
    baselineRef.current = dataUrl || null;
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  const capture = useCallback(() => {
    if (!sigPadRef.current) return;
    const snap = sigPadRef.current.isEmpty()
      ? null
      : sigPadRef.current.getCanvas().toDataURL('image/png');
    setUndoStack((prev) => {
      const next = [...prev, snap];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setRedoStack([]);
  }, [sigPadRef]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return baselineRef.current;
    const popped = undoStack[undoStack.length - 1];
    const nextUndo = undoStack.slice(0, -1);
    const target = nextUndo[nextUndo.length - 1] || baselineRef.current || null;
    setUndoStack(nextUndo);
    setRedoStack((prev) => [...prev, popped]);
    restore(target);
    return target;
  }, [undoStack, restore]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return null;
    const popped = redoStack[redoStack.length - 1];
    const nextRedo = redoStack.slice(0, -1);
    setRedoStack(nextRedo);
    setUndoStack((prev) => [...prev, popped]);
    restore(popped);
    return popped;
  }, [redoStack, restore]);

  const reset = useCallback(() => {
    baselineRef.current = null;
    setUndoStack([]);
    setRedoStack([]);
  }, []);

  return {
    setBaseline,
    capture,
    undo,
    redo,
    reset,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0
  };
};
